/**
 * AdminDashboard - Landing page for admin console
 *
 * Shows:
 * - Stat cards (users, tournaments, leagues, etc.)
 * - Recent scraper runs table
 * - Quick links
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../../services/admin-api';

interface Stats {
  users: number;
  tournaments: number;
  leagues: number;
  leagueMembers: number;
  wrestlers: number;
  activeScraperRuns: number;
  recentScraperFailures: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [brevoSyncing, setBrevoSyncing] = useState(false);
  const [brevoResult, setBrevoResult] = useState<{
    total: number;
    synced: number;
    failed: number;
    errors: string[];
  } | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsData, runsData] = await Promise.all([
        adminApi.getStats(),
        adminApi.listScraperRuns({ limit: 10, offset: 0 }),
      ]);

      setStats(statsData);
      setRecentRuns(runsData.runs);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Users',
      value: stats?.users || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      ),
      color: 'blue',
      onClick: () => navigate('/admin/entities/users'),
    },
    {
      label: 'Tournaments',
      value: stats?.tournaments || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      ),
      color: 'green',
      onClick: () => navigate('/admin/entities/tournaments'),
    },
    {
      label: 'Leagues',
      value: stats?.leagues || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      ),
      color: 'purple',
      onClick: () => navigate('/admin/entities/leagues'),
    },
    {
      label: 'League Members',
      value: stats?.leagueMembers || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      ),
      color: 'indigo',
      onClick: () => navigate('/admin/entities/league_members'),
    },
    {
      label: 'Wrestlers',
      value: stats?.wrestlers || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      ),
      color: 'yellow',
      onClick: () => navigate('/admin/entities/wrestlers'),
    },
    {
      label: 'Active Scraper Runs',
      value: stats?.activeScraperRuns || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      ),
      color: stats?.activeScraperRuns ? 'green' : 'gray',
      onClick: () => navigate('/admin/scraper-runs'),
    },
    {
      label: 'Recent Failures (24h)',
      value: stats?.recentScraperFailures || 0,
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      color: stats?.recentScraperFailures ? 'red' : 'gray',
      onClick: () => navigate('/admin/scraper-runs'),
    },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 mt-2">System overview and quick actions</p>
      </div>

      {/* Stat Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <button
            key={card.label}
            onClick={card.onClick}
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-left border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {card.value.toLocaleString()}
                </p>
              </div>
              <div className={`p-3 bg-${card.color}-100 rounded-full`}>
                <svg
                  className={`w-6 h-6 text-${card.color}-600`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {card.icon}
                </svg>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Recent Scraper Runs */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Scraper Runs</h2>
          <button
            onClick={() => navigate('/admin/scraper-runs')}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View All →
          </button>
        </div>

        {recentRuns.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No scraper runs yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tournament
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentRuns.map((run) => (
                  <tr
                    key={run.id}
                    onClick={() => navigate(`/admin/scraper-runs/${run.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {run.tournament_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {run.scraper_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          run.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : run.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {run.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(run.started_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {run.changes_count || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/admin/entities/tournaments')}
          className="bg-white p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow text-left"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">View All Entities</h3>
          <p className="text-sm text-gray-600">Browse and manage all database entities</p>
        </button>

        <button
          onClick={() => navigate('/admin/scraper-runs')}
          className="bg-white p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow text-left"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Scraper Logs</h3>
          <p className="text-sm text-gray-600">View detailed scraper execution logs</p>
        </button>

        <button
          onClick={() => navigate('/admin/entities/users')}
          className="bg-white p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow text-left"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">User Management</h3>
          <p className="text-sm text-gray-600">View and manage user accounts</p>
        </button>
      </div>

      {/* Brevo Sync */}
      <div className="mt-8 bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Brevo Contact Sync</h2>
            <p className="text-sm text-gray-600 mt-1">
              Sync all users to your Brevo email marketing list
            </p>
          </div>
          <button
            onClick={async () => {
              setBrevoSyncing(true);
              setBrevoResult(null);
              try {
                const result = await adminApi.syncAllToBrevo();
                setBrevoResult(result);
              } catch (error: any) {
                setBrevoResult({
                  total: 0,
                  synced: 0,
                  failed: 1,
                  errors: [error.message],
                });
              } finally {
                setBrevoSyncing(false);
              }
            }}
            disabled={brevoSyncing}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {brevoSyncing ? 'Syncing...' : 'Sync All Users'}
          </button>
        </div>
        {brevoResult && (
          <div
            className={`p-4 rounded-md ${brevoResult.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}
          >
            <p className="text-sm font-medium text-gray-900">
              {brevoResult.synced} of {brevoResult.total} users synced
              {brevoResult.failed > 0 && ` (${brevoResult.failed} failed)`}
            </p>
            {brevoResult.errors.length > 0 && (
              <ul className="mt-2 text-xs text-red-700 space-y-1">
                {brevoResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
